import { useVerTodos } from '@/features/auth/perfil-de-acesso';
import {
  useInboxOrder,
  useTogglePinnedTicket,
  useUserPreferencesValue,
} from '@/features/auth/preferences';
import { ApiError } from '@/shared/lib/api-client';
import { normalizeSearch } from '@/shared/lib/normalize-search';
import { useAuthStore } from '@/shared/stores/auth-store';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, CheckSquare, Loader2, MessageSquarePlus, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  type TicketsOrder,
  type TicketsScope,
  acceptTicket,
  closeTicket,
  markTicketUnread,
  queueTicket,
  reopenTicket,
} from '../api';
import { useTicketsList } from '../hooks';
import { ordenarComRascunhos, useRascunhos } from '../rascunhos';
import { invalidateServerTruthLists, patchTicket } from '../inbox-debug';
import type { TicketStatus, TicketSummary } from '../types';
import { BulkActionBar } from './BulkActionBar';
import { CloseModal } from './CloseModal';
import { FilterPopover, contarFiltrosAtivos } from './FilterPopover';
import { InboxTabs } from './InboxTabs';
import { TicketCard } from './TicketCard';
import { TicketContextMenu } from './TicketContextMenu';
import { TransferModal } from './TransferModal';

const BULK_LIMIT = 50;

interface Props {
  activeStatus: TicketStatus;
  onStatusChange: (s: TicketStatus) => void;
  selectedTicketId: number | null;
  onSelectTicket: (id: number) => void;
  /** Disparado quando o user fecha um ticket via context menu do card.
   *  Parent compara com `selectedTicketId` e limpa o thread se bater. */
  onTicketClosed?: (ticketId: number) => void;
  onNewActive?: () => void;
  scope: TicketsScope;
  onScopeChange: (s: TicketsScope) => void;
  selectedUserId: number | null;
  onSelectedUserIdChange: (id: number | null) => void;
  selectedDepartmentId: number | null;
  onSelectedDepartmentIdChange: (id: number | null) => void;
  selectedChannelId: number | null;
  onSelectedChannelIdChange: (id: number | null) => void;
  onlyUnread: boolean;
  onOnlyUnreadChange: (v: boolean) => void;
  onlyUnanswered: boolean;
  onOnlyUnansweredChange: (v: boolean) => void;
  /** Janela estreita: a lista ocupa a largura toda (sem os 360px fixos). */
  larguraTotal?: boolean;
  /** Janela estreita com conversa aberta: lista escondida (mas montada). */
  oculto?: boolean;
}

export function InboxPanel({
  activeStatus,
  onStatusChange,
  selectedTicketId,
  onSelectTicket,
  onTicketClosed,
  onNewActive,
  scope,
  onScopeChange,
  selectedUserId,
  onSelectedUserIdChange,
  selectedDepartmentId,
  onSelectedDepartmentIdChange,
  selectedChannelId,
  onSelectedChannelIdChange,
  onlyUnread,
  onOnlyUnreadChange,
  onlyUnanswered,
  onOnlyUnansweredChange,
  larguraTotal = false,
  oculto = false,
}: Props) {
  const profile = useAuthStore((s) => s.user?.profile);
  const isAdmin = profile === 'admin';
  const meuId = useAuthStore((s) => s.user?.id);
  const isSupportSession = useAuthStore((s) => Boolean(s.user?.impersonating));
  // Card de COLEGA (aparece com "Visualizar todos" no perfil de acesso): só
  // ver — sem menu de ações e fora da seleção em lote. Admin/suporte seguem
  // com tudo; sem dono (pendente/órfão) e os meus, normal.
  const ehDeColega = (t: TicketSummary): boolean =>
    meuId != null &&
    t.user_id !== null &&
    t.user_id !== meuId &&
    !isAdmin &&
    !isSupportSession;
  // "Visualizar todos" + Usuário: admin ou usuário com a chave "Visualizar
  // tickets de outros usuários", em Ativos OU Fechados — ambas as abas filtram
  // por dono. Pendente não tem dono ainda.
  const podeVerTodos = useVerTodos();
  const showScopeFilter = podeVerTodos && (activeStatus === 'active' || activeStatus === 'closed');
  // Filtros de triagem (não respondidos / não lidas) SÓ na aba Ativos, pra todos
  // os perfis. Pendentes/Fechados continuam como eram (sem triagem).
  const showTriage = activeStatus === 'active';
  // Departamento e Canal: nas 3 abas, pra todos os perfis (dono, 18/out/2026) —
  // então o botão de filtro aparece sempre. O servidor segue aplicando o
  // acesso por departamento por cima (atendente só filtra o que já enxerga).
  const filtrosAtivos = contarFiltrosAtivos({
    showScope: showScopeFilter,
    showTriage,
    scope,
    selectedUserId,
    selectedDepartmentId,
    selectedChannelId,
    onlyUnread,
    onlyUnanswered,
  });
  const [search, setSearch] = useState('');
  const [selectMode, setSelectMode] = useState(false);
  // Ordenação da lista (faixa cinza) — lembrada por usuário (preferências).
  // - 'ultima-mensagem' (padrão): última atividade, o card sobe (WhatsApp);
  // - 'chegada-novos-primeiro' / '-final': ordem de CHEGADA (abertura,
  //   reabertura ou transferência) feita no SERVIDOR, com a paginação — a
  //   posição fica congelada (mensagem e rascunho não mexem);
  // - 'nao-lidas': não lidas sobem (sobre a última atividade, no que está
  //   carregado), mantendo a ordem entre si.
  const [ordem, setOrdem, ordemPronta] = useInboxOrder();
  const porChegada = ordem === 'chegada-novos-primeiro' || ordem === 'chegada-novos-final';
  const ordemDoServidor: TicketsOrder =
    ordem === 'chegada-novos-primeiro'
      ? 'chegada_desc'
      : ordem === 'chegada-novos-final'
        ? 'chegada_asc'
        : 'atividade';
  const [ordemAberta, setOrdemAberta] = useState(false);
  const ordemRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!ordemAberta) return;
    const onDown = (e: MouseEvent): void => {
      if (ordemRef.current && !ordemRef.current.contains(e.target as Node)) setOrdemAberta(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOrdemAberta(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [ordemAberta]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [limitWarning, setLimitWarning] = useState(false);

  const toggleSelect = useCallback((id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        setLimitWarning(false);
      } else {
        if (next.size >= BULK_LIMIT) {
          setLimitWarning(true);
          return prev;
        }
        next.add(id);
        setLimitWarning(false);
      }
      return next;
    });
  }, []);

  const exitSelectMode = (): void => {
    setSelectMode(false);
    setSelected(new Set());
    setLimitWarning(false);
  };

  // Menu de contexto (right-click no card) com atalhos pra Transferir e Encerrar.
  // `target` carrega o ticket inteiro porque os modais precisam de user_id +
  // department_id pra pré-popular selects e contact.display_name pro título.
  const [ctxMenu, setCtxMenu] = useState<{ ticket: TicketSummary; x: number; y: number } | null>(
    null,
  );
  const [transferTarget, setTransferTarget] = useState<TicketSummary | null>(null);
  const [closeTarget, setCloseTarget] = useState<TicketSummary | null>(null);
  const qc = useQueryClient();

  const handleCardContextMenu = useCallback(
    (ticket: TicketSummary, e: React.MouseEvent<HTMLButtonElement>): void => {
      // Modo seleção tem ação própria via checkbox — context menu confunde fluxo.
      // As ações do menu variam por status (pending/active/closed), então todos
      // os tickets abrem o menu; closed mostra só "Retornar para Fila" (reabrir).
      if (selectMode) return;
      e.preventDefault();
      if (ehDeColega(ticket)) return;
      setCtxMenu({ ticket, x: e.clientX, y: e.clientY });
    },
    // biome-ignore lint/correctness/useExhaustiveDependencies: ehDeColega só lê meuId/isAdmin/isSupportSession
    [selectMode, meuId, isAdmin, isSupportSession],
  );

  const closeMut = useMutation({
    mutationFn: ({
      ticketId,
      reason,
      notes,
    }: {
      ticketId: number;
      reason?: number | undefined;
      notes?: string;
    }) =>
      closeTicket(ticketId, {
        ...(reason !== undefined && { closing_reason_id: reason }),
        ...(notes && { notes }),
      }),
    onSuccess: (_data, vars) => {
      // Motor (Fase 2): a lista é atualizada pelo EVENTO com cartão que o
      // servidor emite pra esta ação (o autor também o recebe — está nas
      // dept rooms do ticket). Aqui só as visões que precisam da verdade
      // do servidor (admin 'todos', busca, triagem).
      invalidateServerTruthLists(qc, 'mutation');
      // `refetchType: 'none'` = marca velho SEM buscar. Se este era o ticket
      // aberto, `onTicketClosed` logo abaixo tira ele da tela — buscar
      // cabeçalho e mensagens de algo que ninguém vai olhar é jogar fora.
      // Reabriu depois? Aí busca, porque ficou marcado como velho.
      qc.invalidateQueries({ queryKey: ['ticket', vars.ticketId], refetchType: 'none' });
      qc.invalidateQueries({
        queryKey: ['ticket-messages', vars.ticketId],
        refetchType: 'none',
      });
      // Se o ticket fechado era o aberto no thread, parent limpa pra mostrar
      // o placeholder. Sem isso, conversa ficava órfã (closed) até F5 ou
      // clicar em outro card.
      onTicketClosed?.(vars.ticketId);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        toast.error(err.problem.title, { description: err.problem.detail });
      }
    },
  });

  // Aceitar (pending → active, atribuído a mim). Não limpa o thread: o ticket
  // continua aberto, só muda de aba/status.
  const acceptMut = useMutation({
    mutationFn: (ticketId: number) => acceptTicket(ticketId),
    onSuccess: (_data, ticketId) => {
      // Motor (Fase 2): a lista é atualizada pelo EVENTO com cartão que o
      // servidor emite pra esta ação (o autor também o recebe — está nas
      // dept rooms do ticket). Aqui só as visões que precisam da verdade
      // do servidor (admin 'todos', busca, triagem).
      invalidateServerTruthLists(qc, 'mutation');
      qc.invalidateQueries({ queryKey: ['ticket', ticketId] });
      qc.invalidateQueries({ queryKey: ['ticket-messages', ticketId] });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        toast.error(err.problem.title, { description: err.problem.detail });
      }
    },
  });

  // Retornar para fila (active → pending, tira o dono). Sai da visão atual, então
  // limpa o thread se o ticket devolvido era o aberto.
  const queueMut = useMutation({
    mutationFn: (ticketId: number) => queueTicket(ticketId),
    onSuccess: (_data, ticketId) => {
      // Motor (Fase 2): a lista é atualizada pelo EVENTO com cartão que o
      // servidor emite pra esta ação (o autor também o recebe — está nas
      // dept rooms do ticket). Aqui só as visões que precisam da verdade
      // do servidor (admin 'todos', busca, triagem).
      invalidateServerTruthLists(qc, 'mutation');
      // Mesmo caso do fechar acima: sai da tela, então não busca agora.
      qc.invalidateQueries({ queryKey: ['ticket', ticketId], refetchType: 'none' });
      qc.invalidateQueries({ queryKey: ['ticket-messages', ticketId], refetchType: 'none' });
      onTicketClosed?.(ticketId);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        toast.error(err.problem.title, { description: err.problem.detail });
      }
    },
  });

  /**
   * Marcar como não lida — devolve o selo ao card.
   *
   * FECHA a conversa se era a que estava aberta. Dois motivos: o selo nem
   * aparece no card selecionado (o badge some quando `selected`), então o
   * atendente não veria nada acontecer; e a próxima abertura dispara o
   * `/read`, que zera de volta — deixar aberta é marcar e desmarcar no mesmo
   * gesto. Marcou é "vou cuidar disso depois", então sai da frente.
   */
  const unreadMut = useMutation({
    mutationFn: (ticketId: number) => markTicketUnread(ticketId),
    onMutate: (ticketId: number) => {
      // Otimista: o selo acende na hora. O evento do servidor chega em
      // seguida e confirma (patch idempotente — só mexe se estiver zerado).
      patchTicket(qc, ticketId, (t) => (t.unread_count > 0 ? t : { ...t, unread_count: 1 }), {
        origin: 'unreadMut',
        event: 'click',
      });
      onTicketClosed?.(ticketId);
    },
    onSuccess: () => {
      // Com o filtro "só não lidas" ligado, quem entra na lista é o servidor
      // que decide — as outras visões já foram atendidas pelo patch acima.
      if (onlyUnread) invalidateServerTruthLists(qc, 'mutation');
    },
    onError: (err, ticketId) => {
      // Desfaz o selo otimista: a conversa continua lida.
      patchTicket(qc, ticketId, (t) => (t.unread_count === 0 ? t : { ...t, unread_count: 0 }), {
        origin: 'unreadMut:erro',
        event: 'click',
      });
      if (err instanceof ApiError) {
        toast.error(err.problem.title, { description: err.problem.detail });
      } else {
        toast.error('Não foi possível marcar como não lida', {
          description: 'Tente novamente em alguns segundos.',
        });
      }
    },
  });

  // Reabrir (closed → pending). Sai da aba Fechados, então limpa o thread se
  // era o aberto. Trata CONTACT_HAS_ACTIVE_TICKET com a mensagem do backend.
  const reopenMut = useMutation({
    mutationFn: (ticketId: number) => reopenTicket(ticketId),
    onSuccess: (_data, ticketId) => {
      // Motor (Fase 2): a lista é atualizada pelo EVENTO com cartão que o
      // servidor emite pra esta ação (o autor também o recebe — está nas
      // dept rooms do ticket). Aqui só as visões que precisam da verdade
      // do servidor (admin 'todos', busca, triagem).
      invalidateServerTruthLists(qc, 'mutation');
      // Mesmo caso do fechar acima: sai da tela, então não busca agora.
      qc.invalidateQueries({ queryKey: ['ticket', ticketId], refetchType: 'none' });
      qc.invalidateQueries({ queryKey: ['ticket-messages', ticketId], refetchType: 'none' });
      onTicketClosed?.(ticketId);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        const detail = err.problem.detail;
        toast.error(err.problem.title, detail ? { description: detail } : undefined);
      } else {
        toast.error('Não foi possível reabrir o atendimento', {
          description: 'Tente novamente em alguns segundos.',
        });
      }
    },
  });

  // Modo seleção não faz sentido em "Fechados" (bulk-close e bulk-tag não se
  // aplicam a tickets já encerrados). Se o user troca pra Fechados estando em
  // modo seleção, sai automático pra evitar estado órfão (BulkActionBar com
  // ações que dariam erro no backend).
  const isClosedTab = activeStatus === 'closed';
  useEffect(() => {
    if (isClosedTab && selectMode) {
      exitSelectMode();
    }
  }, [isClosedTab, selectMode]);

  // Debounce 300ms na busca pra evitar GET /tickets a cada tecla. Sem isso,
  // digitar "isabelle" dispararia 8 requests; com debounce, só 1 quando user
  // pausa de digitar. Aplicado SOMENTE à aba ativa (outras 2 mantêm sem
  // filtro pra preservar contagens corretas e cache pré-carregado).
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(id);
  }, [search]);
  const qForActive = activeStatus === 'active' ? debouncedSearch : '';
  const qForPending = activeStatus === 'pending' ? debouncedSearch : '';
  const qForClosed = activeStatus === 'closed' ? debouncedSearch : '';

  // Hook por status — useInfiniteQuery, batches de 100. Scope + userId
  // aplicam em 'active' E 'closed' (admin pode ver tudo ou filtrar por user).
  // Pending: sem dono, então sem scope/user. Departamento e canal valem nas 3.
  // Triagem só vale em Ativos → só o hook de 'active' recebe esses dois.
  const activeQ = useTicketsList(
    'active',
    scope,
    selectedUserId,
    selectedDepartmentId,
    qForActive,
    onlyUnread,
    onlyUnanswered,
    selectedChannelId,
    ordemDoServidor,
    ordemPronta,
  );
  const pendingQ = useTicketsList(
    'pending',
    undefined,
    undefined,
    selectedDepartmentId,
    qForPending,
    false,
    false,
    selectedChannelId,
    ordemDoServidor,
    ordemPronta,
  );
  const closedQ = useTicketsList(
    'closed',
    scope,
    selectedUserId,
    selectedDepartmentId,
    qForClosed,
    false,
    false,
    selectedChannelId,
    ordemDoServidor,
    ordemPronta,
  );

  // Badge usa `total` da 1ª page (count de toda a aba, não só do que tá
  // carregado em memória).
  const counts = {
    active: activeQ.data?.pages[0]?.total ?? 0,
    pending: pendingQ.data?.pages[0]?.total ?? 0,
    closed: closedQ.data?.pages[0]?.total ?? 0,
  };

  // Flatten das pages do infinite query.
  const activeList = useMemo(
    () => activeQ.data?.pages.flatMap((p) => p.tickets) ?? [],
    [activeQ.data],
  );
  const pendingList = useMemo(
    () => pendingQ.data?.pages.flatMap((p) => p.tickets) ?? [],
    [pendingQ.data],
  );
  const closedList = useMemo(
    () => closedQ.data?.pages.flatMap((p) => p.tickets) ?? [],
    [closedQ.data],
  );

  // Até a ordem escolhida chegar, nada de lista (nem a guardada no navegador,
  // que pode estar na ordem antiga) — aparece "Carregando…".
  const list = !ordemPronta
    ? []
    : activeStatus === 'active'
      ? activeList
      : activeStatus === 'pending'
        ? pendingList
        : closedList;

  const activeQuery =
    activeStatus === 'active' ? activeQ : activeStatus === 'pending' ? pendingQ : closedQ;

  const filtered = useMemo(() => {
    if (!search.trim()) return list;
    // Busca insensível a acento + caixa: "acai" acha "Açaí".
    const q = normalizeSearch(search);
    return list.filter(
      (t) =>
        normalizeSearch(t.contact.display_name ?? '').includes(q) ||
        normalizeSearch(t.contact.identifier?.value ?? '').includes(q) ||
        normalizeSearch(t.last_message_preview ?? '').includes(q) ||
        normalizeSearch(t.protocol_number ?? '').includes(q),
    );
  }, [list, search]);

  // Conversas fixadas (pessoal, users.preferences — leitor: AppShell/Topbar
  // mantêm a query viva). Só a aba Ativos fixa; nas outras o set fica inerte.
  const prefsQ = useUserPreferencesValue();
  const togglePin = useTogglePinnedTicket();
  const pinnedSet = useMemo(
    () => new Set(prefsQ.data?.pinnedTicketIds ?? []),
    [prefsQ.data?.pinnedTicketIds],
  );

  // Fixadas primeiro (partição estável): entre fixadas e entre normais, a
  // ordenação do servidor (última mensagem) segue intacta — o motor de
  // realtime encaixa no array subjacente e a partição só levanta as fixadas.
  // Rascunhos (texto deixado no compositor): a conversa sobe como se tivesse
  // acabado de enviar, na hora em que o rascunho foi deixado; apagou, volta
  // pra origem. Só reordena — não invalida nem toca o cache do servidor.
  const rascunhos = useRascunhos((s) => s.porTicket);
  const displayList = useMemo(() => {
    // Ordem de chegada: a posição vem do servidor e fica CONGELADA — nem o
    // rascunho mexe (dono, 18/out/2026). Nas demais, o rascunho sobe a
    // conversa como se tivesse acabado de enviar.
    const base = porChegada ? filtered : ordenarComRascunhos(filtered, rascunhos);
    // "Não lidas primeiro": partição estável — entre não lidas e entre lidas
    // a ordem do servidor segue intacta.
    const ordenada =
      ordem === 'nao-lidas'
        ? [...base.filter((t) => t.unread_count > 0), ...base.filter((t) => t.unread_count === 0)]
        : base;
    if (activeStatus !== 'active' || pinnedSet.size === 0) return ordenada;
    const pinned: TicketSummary[] = [];
    const rest: TicketSummary[] = [];
    for (const t of ordenada) (pinnedSet.has(t.id) ? pinned : rest).push(t);
    return [...pinned, ...rest];
  }, [filtered, rascunhos, activeStatus, pinnedSet, ordem, porChegada]);

  const isLoading =
    !ordemPronta || activeQ.isLoading || pendingQ.isLoading || closedQ.isLoading;

  // IntersectionObserver pra trigger fetchNextPage quando a sentinela aparece
  // no viewport. `rootMargin: 200px` pré-carrega antes de chegar ao fim.
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        if (
          entries.some((e) => e.isIntersecting) &&
          activeQuery.hasNextPage &&
          !activeQuery.isFetchingNextPage
        ) {
          void activeQuery.fetchNextPage();
        }
      },
      { rootMargin: '200px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [activeQuery.hasNextPage, activeQuery.isFetchingNextPage, activeQuery.fetchNextPage]);

  return (
    <aside
      className={`flex flex-col border-n-200 border-r bg-n-0 ${
        larguraTotal ? 'min-w-0 flex-1' : 'w-[360px] min-w-[360px]'
      } ${oculto ? 'hidden' : ''}`}
    >
      <InboxTabs active={activeStatus} counts={counts} onChange={onStatusChange} />

      <div className="flex gap-[8px] border-n-200 border-b px-[12px] py-[10px]">
        {/* Campo de busca — o <input> é o próprio pill (focus ring nativo
            contorna tudo, incluindo a lupa). O filtro agora fica FORA, como
            botão próprio ao lado (não mais dentro do campo). */}
        <div className="relative flex-1">
          <Search
            size={14}
            strokeWidth={1.75}
            className="pointer-events-none absolute top-1/2 left-[13px] -translate-y-1/2 text-n-400"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar atendimento…"
            className={`h-[33px] w-full rounded-pill border bg-n-0 pr-9 pl-[32px] text-[13px] text-n-900 outline-none placeholder:text-n-400 ${
              filtrosAtivos > 0 ? 'border-brand-200' : 'border-n-200'
            }`}
          />
          {search.trim() && activeQuery.isFetching && (
            <Loader2
              size={14}
              className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 animate-spin text-n-400"
            />
          )}
        </div>

        {/* Filtro avançado — botão solto (fora do campo), nas 3 abas
            (departamento e canal valem em todas, pra todos os perfis). */}
        <FilterPopover
          standalone
          showScope={showScopeFilter}
          showTriage={showTriage}
          scope={scope}
          onScopeChange={onScopeChange}
          selectedUserId={selectedUserId}
          onSelectedUserIdChange={onSelectedUserIdChange}
          selectedDepartmentId={selectedDepartmentId}
          onSelectedDepartmentIdChange={onSelectedDepartmentIdChange}
          selectedChannelId={selectedChannelId}
          onSelectedChannelIdChange={onSelectedChannelIdChange}
          onlyUnread={onlyUnread}
          onOnlyUnreadChange={onOnlyUnreadChange}
          onlyUnanswered={onlyUnanswered}
          onOnlyUnansweredChange={onOnlyUnansweredChange}
        />

        <button
          type="button"
          onClick={onNewActive}
          disabled={!onNewActive}
          className="flex h-[33px] w-[34px] flex-shrink-0 items-center justify-center rounded-md text-n-600 transition-colors hover:bg-n-100 hover:text-n-800 disabled:opacity-40"
          aria-label="Novo atendimento"
          title="Novo atendimento ativo"
        >
          <MessageSquarePlus size={18} strokeWidth={1.75} />
        </button>
      </div>

      {/* Faixa cinza entre a busca e a lista. À direita: selecionar (só
          Ativos/Pendentes — em Fechados não há ação em massa) e ordenar. No
          modo seleção, "Selecionar todos" e o contador entram nela também. */}
      <div className="flex items-center gap-[2px] border-n-200 border-b bg-n-100 px-[10px] py-px">
        {selectMode && !isClosedTab && (
          <button
            type="button"
            onClick={() => {
              // displayList (não filtered): "visíveis" segue a ordem exibida,
              // com fixados primeiro — o que o usuário vê é o que seleciona.
              // Cards de colega (só visualização) não entram no lote.
              const visibleIds = displayList
                .filter((t) => !ehDeColega(t))
                .slice(0, BULK_LIMIT)
                .map((t) => t.id);
              const allSelected = visibleIds.every((id) => selected.has(id));
              if (allSelected) {
                // Desmarca todos os visíveis (preserva seleções fora do filtro atual).
                setSelected((prev) => {
                  const next = new Set(prev);
                  visibleIds.forEach((id) => next.delete(id));
                  return next;
                });
                setLimitWarning(false);
              } else {
                // Marca todos os visíveis até BULK_LIMIT. Se total > limite, marca
                // só os primeiros BULK_LIMIT e mostra warning.
                setSelected((prev) => {
                  const next = new Set(prev);
                  for (const id of visibleIds) {
                    if (next.size >= BULK_LIMIT) break;
                    next.add(id);
                  }
                  return next;
                });
                if (filtered.length > BULK_LIMIT) setLimitWarning(true);
              }
            }}
            className="font-medium text-[12px] text-brand-600 hover:text-brand-700"
          >
            {displayList.some((t) => !ehDeColega(t)) &&
            displayList
              .filter((t) => !ehDeColega(t))
              .slice(0, BULK_LIMIT)
              .every((t) => selected.has(t.id))
              ? 'Desmarcar todos'
              : 'Selecionar todos'}
          </button>
        )}
        <div className="ml-auto flex items-center gap-[2px]">
          {selectMode && !isClosedTab && (
            <span className="mr-[8px] text-[12px] text-n-500">
              {selected.size} de{' '}
              {Math.min(filtered.filter((t) => !ehDeColega(t)).length, BULK_LIMIT)} selecionados
            </span>
          )}
          {!isClosedTab && (
            <button
              type="button"
              onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
              aria-pressed={selectMode}
              aria-label={selectMode ? 'Sair do modo seleção' : 'Selecionar todos'}
              title={selectMode ? 'Sair do modo seleção' : 'Selecionar todos'}
              className={`flex h-[28px] w-[28px] items-center justify-center rounded-md transition-colors ${
                // Ativo no mesmo padrão do filtro ligado (azul-claro + ícone
                // azul) — o azul sólido "estourava" na faixa cinza.
                selectMode
                  ? 'bg-brand-50 text-brand-600 hover:bg-brand-100'
                  : 'text-n-500 hover:bg-n-0 hover:text-n-700'
              }`}
            >
              <CheckSquare size={16} strokeWidth={1.75} />
            </button>
          )}
          <div ref={ordemRef} className="relative">
            <button
              type="button"
              onClick={() => setOrdemAberta((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={ordemAberta}
              aria-label="Ordenar"
              title="Ordenar"
              // Destaque só com o menu aberto — a ordem escolhida já aparece na
              // própria lista, o ícone não precisa ficar marcado.
              className={`flex h-[28px] w-[28px] items-center justify-center rounded-md transition-colors ${
                ordemAberta ? 'bg-n-0 text-brand-600' : 'text-n-500 hover:bg-n-0 hover:text-n-700'
              }`}
            >
              {/* Três linhas decrescentes + seta pra baixo (mesmo traço do mockup). */}
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.75}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M3 6h11M3 12h8M3 18h5M18 5v14M15 16l3 3 3-3" />
              </svg>
            </button>
            {ordemAberta && (
              <div
                role="menu"
                aria-label="Ordenar"
                // Mesma largura do popover de filtros (280px); rótulo longo quebra.
                className="absolute top-[calc(100%+4px)] right-0 z-30 w-[280px] rounded-md border border-n-200 bg-n-0 p-[6px] shadow-md"
              >
                {(
                  [
                    { id: 'chegada-novos-primeiro', label: 'Por ordem de chegada (mais novos primeiro)' },
                    // Padrão (quem nunca escolheu): igual ao WhatsApp. Segunda na
                    // lista por pedido do dono (18/out/2026) — a posição no menu
                    // não muda o padrão.
                    { id: 'ultima-mensagem', label: 'Última mensagem mais recente primeiro' },
                    { id: 'chegada-novos-final', label: 'Por ordem de chegada (mais novos ao final)' },
                    { id: 'nao-lidas', label: 'Não lidas primeiro' },
                  ] as const
                ).map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    role="menuitemradio"
                    aria-checked={ordem === o.id}
                    onClick={() => {
                      setOrdem(o.id);
                      setOrdemAberta(false);
                    }}
                    // Mesma tipografia das linhas do popover de filtros: 13px
                    // regular, n-700, linha de 30px (cresce se o rótulo quebrar;
                    // o check fica alinhado à primeira linha).
                    className="flex min-h-[30px] w-full items-start gap-[8px] rounded-sm px-[8px] py-[5px] text-left text-[13px] text-n-700 transition-colors hover:bg-n-100"
                  >
                    <Check
                      size={14}
                      strokeWidth={2.2}
                      className={`mt-[3px] flex-shrink-0 ${ordem === o.id ? 'text-brand-600' : 'invisible'}`}
                      aria-hidden="true"
                    />
                    {o.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {limitWarning && (
        <div className="border-warning-200 border-b bg-warning-50 px-4 py-1.5 text-[12px] text-warning-700">
          Máximo de {BULK_LIMIT} tickets por vez.
        </div>
      )}

      {/* Lista colada (estilo WhatsApp): sem espaço nem margem entre cards —
          cada card traz o próprio divisor embaixo. */}
      <div className="flex flex-1 flex-col overflow-y-auto">
        {isLoading && filtered.length === 0 && (
          <div className="px-3 py-6 text-center text-[13px] text-n-500">Carregando…</div>
        )}
        {!isLoading && filtered.length === 0 && (
          <div className="flex flex-col items-center gap-2 px-6 py-12 text-center text-n-500">
            <div className="flex h-14 w-14 items-center justify-center rounded-pill bg-brand-50 text-brand-600">
              ✓
            </div>
            <h3 className="font-semibold text-[15px] text-n-700">
              Todos os atendimentos estão em dia
            </h3>
            <p className="max-w-[260px] text-[13px]">
              Nada na aba <strong>{labelFor(activeStatus)}</strong> agora. Bom trabalho!
            </p>
          </div>
        )}
        {displayList.map((t) => {
          // Defesa em profundidade: mesmo se algo deixar selectMode=true em
          // tab fechada (ex: troca rápida de aba), nunca habilita seleção
          // visual nos cards. O useEffect já cuida do estado, mas aqui
          // garante a UI consistente.
          const cardSelectable = selectMode && !isClosedTab && !ehDeColega(t);
          return (
            <TicketCard
              key={t.id}
              ticket={t}
              selected={selectedTicketId === t.id}
              selectable={cardSelectable}
              checked={selected.has(t.id)}
              onCheckChange={() => toggleSelect(t.id)}
              onClick={() => (cardSelectable ? toggleSelect(t.id) : onSelectTicket(t.id))}
              onContextMenu={(e) => handleCardContextMenu(t, e)}
              pinned={pinnedSet.has(t.id)}
            />
          );
        })}
        {/* Sentinela do infinite scroll — quando entra no viewport, dispara
            fetchNextPage. Funciona TAMBÉM durante busca: agora o backend
            retorna resultados paginados (server-side search), então scrollar
            continua trazendo mais matches. */}
        {activeQuery.hasNextPage && (
          <div ref={sentinelRef} className="flex justify-center py-3">
            {activeQuery.isFetchingNextPage ? (
              <span className="text-[12px] text-n-500">Carregando mais…</span>
            ) : (
              <span className="text-[11px] text-n-400">↓</span>
            )}
          </div>
        )}
      </div>
      {selectMode && selected.size > 0 && (
        <BulkActionBar
          ticketIds={Array.from(selected)}
          /* Basta um atendimento de canal que exige pra o motivo continuar
             obrigatório no lote — a API aplica a mesma regra. */
          exigeMotivo={displayList.some(
            (t) => selected.has(t.id) && (t.whatsapp_number.require_closing_reason ?? true),
          )}
          onClear={exitSelectMode}
          /* Conversa aberta entre as encerradas/transferidas sai da tela,
             igual ao encerrar 1 (o handler ignora id que não é o aberto). */
          onTicketsLeft={(ids) => {
            for (const id of ids) onTicketClosed?.(id);
          }}
        />
      )}

      {ctxMenu && (
        <TicketContextMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          status={ctxMenu.ticket.status}
          title={ctxMenu.ticket.contact.display_name ?? 'Sem nome'}
          onDismiss={() => setCtxMenu(null)}
          onAccept={() => acceptMut.mutate(ctxMenu.ticket.id)}
          onQueue={() => queueMut.mutate(ctxMenu.ticket.id)}
          onTransfer={() => setTransferTarget(ctxMenu.ticket)}
          onClose={() => setCloseTarget(ctxMenu.ticket)}
          onReopen={() => reopenMut.mutate(ctxMenu.ticket.id)}
          // Fixar só na aba Ativos (único gesto de pin).
          pinned={pinnedSet.has(ctxMenu.ticket.id)}
          onTogglePin={activeStatus === 'active' ? () => togglePin(ctxMenu.ticket.id) : undefined}
          // Some do menu quando já está não-lida — não teria o que fazer.
          onMarkUnread={
            ctxMenu.ticket.unread_count > 0 ? undefined : () => unreadMut.mutate(ctxMenu.ticket.id)
          }
        />
      )}

      {transferTarget && (
        <TransferModal
          ticketId={transferTarget.id}
          currentUserId={transferTarget.user_id}
          currentDepartmentId={transferTarget.department_id}
          open={true}
          onClose={() => setTransferTarget(null)}
          onTransferred={() => {
            // Transferido sai da minha visão — limpa o thread se era o aberto.
            onTicketClosed?.(transferTarget.id);
            setTransferTarget(null);
          }}
        />
      )}

      <CloseModal
        open={closeTarget !== null}
        exigeMotivo={closeTarget?.whatsapp_number.require_closing_reason ?? true}
        onClose={() => setCloseTarget(null)}
        onConfirm={async (reason, notes) => {
          if (!closeTarget) return;
          await closeMut.mutateAsync({
            ticketId: closeTarget.id,
            reason,
            ...(notes && { notes }),
          });
        }}
      />
    </aside>
  );
}

function labelFor(s: TicketStatus): string {
  switch (s) {
    case 'active':
      return 'Ativos';
    case 'pending':
      return 'Pendentes';
    case 'closed':
      return 'Fechados';
  }
}
